import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const HealthMeter = ({ healthStatus, score = 0, size = 300 }) => {
  const indicatorRef = useRef(null);
  const meterRef = useRef(null);

  // Convert health status or score to meter value (0-100)
  const getMeterValue = () => {
    // If we have a numeric score, use it directly
    if (typeof score === 'number' && score > 0) {
      return Math.min(100, Math.max(0, score));
    }

    // Otherwise map health status to score
    const statusToScore = {
      'excellent': 90,
      'good': 75,
      'fair': 60,
      'at risk': 40,
      'needs attention': 40,
      'high risk': 25,
      'critical': 10
    };

    return statusToScore[healthStatus?.toLowerCase()] || 0;
  };

  // Get color based on score
  const getHealthColor = (value) => {
    if (value >= 80) return '#28a745'; // Green for excellent
    if (value >= 70) return '#20c997'; // Teal for good  
    if (value >= 60) return '#ffc107'; // Yellow for fair
    if (value >= 40) return '#fd7e14'; // Orange for at risk
    if (value >= 20) return '#dc3545'; // Red for high risk
    return '#6f42c1'; // Purple for critical
  };

  // Get health label based on score
  const getHealthLabel = (value) => {
    if (value >= 80) return 'Excellent';
    if (value >= 70) return 'Good';
    if (value >= 60) return 'Fair';
    if (value >= 40) return 'At Risk';
    if (value >= 20) return 'High Risk';
    return 'Critical';
  };

  const meterValue = getMeterValue();
  const healthColor = getHealthColor(meterValue);
  const healthLabel = getHealthLabel(meterValue);

  // Define the health segments with their ranges
  const segments = [
    { start: 0, end: 20, color: '#6f42c1', label: 'Critical' },
    { start: 20, end: 40, color: '#dc3545', label: 'High Risk' },
    { start: 40, end: 60, color: '#fd7e14', label: 'At Risk' },
    { start: 60, end: 70, color: '#ffc107', label: 'Fair' },
    { start: 70, end: 80, color: '#20c997', label: 'Good' },
    { start: 80, end: 100, color: '#28a745', label: 'Excellent' }
  ];

  useEffect(() => {
    if (!indicatorRef.current || !meterRef.current) return;
    
    // Calculate position for indicator (percentage of total width)
    const meterWidth = size - 40; // Account for padding
    const indicatorPosition = (meterValue / 100) * meterWidth;
    indicatorRef.current.style.left = `${20 + indicatorPosition}px`;
  }, [meterValue, size]);

  const barHeight = 30;
  const totalHeight = 120;

  return (
    <div className="health-meter" style={{ 
      width: `${size}px`, 
      height: `${totalHeight}px`, 
      margin: '0 auto',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Health status labels above the meter */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: `${size - 40}px`,
        marginBottom: '8px',
        fontSize: '10px',
        fontWeight: '600'
      }}>
        {segments.map((segment, index) => (
          <div
            key={segment.label}
            style={{
              color: segment.color,
              textAlign: 'center',
              opacity: getHealthLabel(meterValue) === segment.label ? 1 : 0.6,
              fontWeight: getHealthLabel(meterValue) === segment.label ? 'bold' : '600',
              fontSize: getHealthLabel(meterValue) === segment.label ? '11px' : '10px'
            }}
          >
            {segment.label}
          </div>
        ))}
      </div>

      {/* Main meter container */}
      <div 
        ref={meterRef}
        style={{
          position: 'relative',
          width: `${size - 40}px`,
          height: `${barHeight}px`,
          borderRadius: '15px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '2px solid #fff'
        }}
      >
        {/* Colored segments */}
        <div style={{
          display: 'flex',
          height: '100%',
          width: '100%'
        }}>
          {segments.map((segment, index) => {
            const segmentWidth = ((segment.end - segment.start) / 100) * 100;
            return (
              <div
                key={index}
                style={{
                  width: `${segmentWidth}%`,
                  height: '100%',
                  backgroundColor: segment.color,
                  borderRight: index < segments.length - 1 ? '1px solid rgba(255,255,255,0.3)' : 'none'
                }}
              />
            );
          })}
        </div>

        {/* Functional indicator */}
        <div
          ref={indicatorRef}
          style={{
            position: 'absolute',
            top: '-8px',
            transform: 'translateX(-50%)',
            transition: 'left 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 10
          }}
        >
          {/* Indicator arrow */}
          <div style={{
            width: '0',
            height: '0',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '12px solid #333',
            marginBottom: '2px'
          }} />
          
          {/* Indicator line */}
          <div style={{
            width: '3px',
            height: `${barHeight + 16}px`,
            backgroundColor: '#333',
            margin: '0 auto',
            borderRadius: '2px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }} />
          
          {/* Indicator base */}
          <div style={{
            width: '12px',
            height: '6px',
            backgroundColor: '#333',
            borderRadius: '0 0 6px 6px',
            margin: '0 auto',
            marginTop: '-2px'
          }} />
        </div>
      </div>

      {/* Score and status display below */}
      <div style={{
        marginTop: '15px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: healthColor,
          lineHeight: 1
        }}>
          {Math.round(meterValue)}
        </div>
        <div style={{
          fontSize: '12px',
          color: '#6c757d',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginTop: '2px'
        }}>
          Health Score
        </div>
        <div style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: healthColor,
          marginTop: '4px'
        }}>
          {healthLabel}
        </div>
      </div>

      {/* Scale markers below */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: `${size - 40}px`,
        marginTop: '8px',
        fontSize: '9px',
        color: '#999'
      }}>
        <span>0</span>
        <span>20</span>
        <span>40</span>
        <span>60</span>
        <span>80</span>
        <span>100</span>
      </div>
    </div>
  );
};

HealthMeter.propTypes = {
  healthStatus: PropTypes.string,
  score: PropTypes.number,
  size: PropTypes.number
};

HealthMeter.defaultProps = {
  healthStatus: 'unknown',
  score: 0,
  size: 300
};

export default HealthMeter;
